import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Menu,
  Tabs,
  Tab,
  Typography,
  Chip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { currentAccessLevel } from '../../utils/sessionAccess';
import {
  Person as ProfileIcon,
  Logout as LogoutIcon,
  Article as ArticleIcon,
  Dashboard as DashboardIcon,
  Groups as GroupCodeIcon,
  LocationCity as TownVillageIcon,
  PersonOutline as CasteIcon,
  AccountBox as LedgerIcon,
  ConfirmationNumber as TicketIcon,
  Add as CreatePledgeIcon,
  ReceiptLong as ReceiptIcon,
  Receipt as ReleaseIcon,
  BugReport as BugReportIcon,
  EditNote as ManualEntryIcon,
  AccountBalance as CashBalanceIcon,
  Fingerprint as FingerprintIcon,
  CloudUpload as ImportStatusIcon,
  Assessment as ReportsIcon,
  Print as PrintIcon,
  Description as PrintConditionIcon,
  CalendarMonth as FinancialYearIcon,
  Percent as GovInterestIcon,
  ListAlt as ListIcon,
  Gavel as AuctionIcon,
  MenuBook as BookIcon,
  Payments as PaymentIcon,
  Calculate as CalculateIcon,
  Balance as BalanceIcon,
  KeyboardArrowDown as ArrowDownIcon,
  DarkMode as BlackIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';

const menuPaperSx = {
  mt: 1.25,
  overflow: 'hidden',
  borderRadius: 3,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 22px 48px rgba(15, 23, 42, 0.22)',
  background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  minWidth: 320,
};

function MenuHeader({ title, subtitle }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        background: 'linear-gradient(115deg, #0b1220 0%, #16233a 55%, #1e3a5f 100%)',
        borderBottom: '1px solid rgba(212,175,106,0.28)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'rgba(245,230,200,0.65)',
            mb: 0.25,
          }}
        >
          {subtitle}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#f8fafc', letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      <Chip
        size="small"
        label="GIRVI"
        sx={{
          height: 22,
          fontWeight: 800,
          fontSize: 10,
          bgcolor: 'rgba(240,217,160,0.14)',
          color: '#f5e6c8',
          border: '1px solid rgba(240,217,160,0.35)',
        }}
      />
    </Box>
  );
}

function NavMenuItem({ icon, label, hint, onClick, accent = '#c9a24d' }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        px: 1.5,
        py: 1.05,
        borderRadius: 2,
        transition: 'background 0.15s ease, transform 0.15s ease',
        '&:hover': {
          background: 'linear-gradient(90deg, rgba(201,162,77,0.12), rgba(15,23,42,0.03))',
          transform: 'translateX(2px)',
          '& .nav-ico': {
            bgcolor: '#0f172a',
            color: '#f0d9a0',
            borderColor: 'rgba(240,217,160,0.45)',
          },
        },
      }}
    >
      <Box
        className="nav-ico"
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          bgcolor: '#f1f5f9',
          color: '#334155',
          border: '1px solid #e2e8f0',
          transition: 'all 0.15s ease',
          '& svg': { fontSize: 18, color: accent },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a', lineHeight: 1.2 }}>
          {label}
        </Typography>
        {hint ? (
          <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.15, lineHeight: 1.2 }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const sectionLabelSx = {
  px: 1.75,
  pt: 1.35,
  pb: 0.65,
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#94a3b8',
};

function NavDropdown({ id, anchorEl, onClose, title, subtitle, width = 360, children }) {
  return (
    <Menu
      id={id}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      transitionDuration={160}
      MenuListProps={{
        sx: { p: 0, outline: 'none' },
        autoFocusItem: false,
      }}
      PaperProps={{
        elevation: 0,
        sx: { ...menuPaperSx, width, maxWidth: 'min(96vw, 560px)' },
      }}
    >
      <MenuHeader title={title} subtitle={subtitle} />
      <Box sx={{ p: 1, maxHeight: 'min(72vh, 520px)', overflowY: 'auto' }}>{children}</Box>
    </Menu>
  );
}

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = currentAccessLevel();
  const isBlackSession = userType === 'black';
  const [masterAnchorEl, setMasterAnchorEl] = useState(null);
  const [pledgeAnchorEl, setPledgeAnchorEl] = useState(null);
  const [transactionsAnchorEl, setTransactionsAnchorEl] = useState(null);
  const [accountingAnchorEl, setAccountingAnchorEl] = useState(null);
  const [auctionAnchorEl, setAuctionAnchorEl] = useState(null);
  const [blackAnchorEl, setBlackAnchorEl] = useState(null);

  const getActiveTab = () => {
    const pathname = location.pathname;
    if (pathname.startsWith('/master') || pathname === '/import-status' || pathname === '/sync-status') return 0;
    if (pathname.startsWith('/pledge') || pathname.startsWith('/release') || pathname.startsWith('/receipts')) return 1;
    if (pathname === '/profile') return 2;
    if (
      pathname.startsWith('/ledgers') ||
      pathname.startsWith('/daybook') ||
      pathname.startsWith('/journal-register') ||
      pathname.startsWith('/pledge-book') ||
      pathname.startsWith('/profit-loss') ||
      pathname.startsWith('/balance-sheet') ||
      pathname.startsWith('/trial-balance') ||
      pathname.startsWith('/debug-accounting')
    )
      return 3;
    if (
      pathname.startsWith('/manual-entry') ||
      pathname.startsWith('/payments') ||
      pathname.startsWith('/cash-balance') ||
      pathname.startsWith('/journal-voucher') ||
      pathname.startsWith('/interest-calculation')
    )
      return 4;
    if (pathname.startsWith('/auction')) return 5;
    if (pathname.startsWith('/reports')) return 6;
    if (
      pathname.startsWith('/black') ||
      pathname.startsWith('/illegal-cash-balance')
    )
      return 7;
    return false;
  };

  const [value, setValue] = useState(getActiveTab());

  useEffect(() => {
    setValue(getActiveTab());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleChange = (_event, newValue) => setValue(newValue);

  const closeAll = () => {
    setMasterAnchorEl(null);
    setPledgeAnchorEl(null);
    setTransactionsAnchorEl(null);
    setAccountingAnchorEl(null);
    setAuctionAnchorEl(null);
    setBlackAnchorEl(null);
  };

  const go = (path) => {
    navigate(path);
    closeAll();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    navigate('/login');
  };

  const goDashboard = () => {
    setValue(false);
    navigate(isBlackSession ? '/black' : '/');
  };

  const tabValue = typeof value === 'number' && value >= 0 ? Math.min(value, isBlackSession ? 7 : 6) : false;

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: 'linear-gradient(105deg, #0b1220 0%, #152238 55%, #1a2f4a 100%)',
        color: '#e2e8f0',
        borderBottom: '1px solid rgba(212, 175, 106, 0.35)',
        boxShadow: '0 4px 18px rgba(8, 15, 28, 0.28)',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          minHeight: '56px !important',
          py: 0.5,
          px: { xs: 1.25, md: 2 },
        }}
      >
        <Button
          onClick={goDashboard}
          sx={{
            minWidth: 'auto',
            px: 1,
            py: 0.75,
            borderRadius: 2,
            textTransform: 'none',
            color: '#f5e6c8',
            gap: 1,
            flexShrink: 0,
            '&:hover': { background: 'rgba(212, 175, 106, 0.14)' },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '9px',
              display: 'grid',
              placeItems: 'center',
              background: 'linear-gradient(145deg, #f0d9a0, #c9a24d)',
              color: '#1a1408',
              fontWeight: 900,
              fontSize: 13,
              boxShadow: '0 3px 10px rgba(201, 162, 77, 0.35)',
            }}
          >
            G
          </Box>
          <Box sx={{ textAlign: 'left', lineHeight: 1.05 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 17, letterSpacing: '0.12em', color: '#f5e6c8' }}>
              GIRVI
            </Typography>
            <Typography
              sx={{
                fontSize: 9,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(226,232,240,0.55)',
              }}
            >
              {isBlackSession ? 'Black' : 'Dashboard'}
            </Typography>
          </Box>
        </Button>

        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="navigation tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 44,
            '& .MuiTabs-flexContainer': { gap: 0.25 },
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(90deg, #c9a24d, #f0d9a0)',
              height: 2.5,
              borderRadius: 2,
            },
            '& .MuiTab-root': {
              color: 'rgba(226,232,240,0.8)',
              textTransform: 'none',
              fontWeight: 650,
              fontSize: '0.92rem',
              minHeight: 44,
              minWidth: 'auto',
              px: 1.35,
              '&.Mui-selected': { color: '#f8e7c0' },
              '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.06)' },
            },
            '& .MuiTabs-scrollButtons': { color: '#f5e6c8' },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                Master <ArrowDownIcon sx={{ fontSize: 16, opacity: 0.75 }} />
              </Box>
            }
            onClick={(e) => setMasterAnchorEl(e.currentTarget)}
            aria-controls="master-menu"
            aria-haspopup="true"
          />
          <Tab
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                Pledge <ArrowDownIcon sx={{ fontSize: 16, opacity: 0.75 }} />
              </Box>
            }
            onClick={(e) => setPledgeAnchorEl(e.currentTarget)}
            aria-controls="pledge-menu"
            aria-haspopup="true"
          />
          <Tab
            label="Profile"
            onClick={() => {
              navigate('/profile');
              setValue(2);
            }}
            icon={<ProfileIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
          <Tab
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                Transactions <ArrowDownIcon sx={{ fontSize: 16, opacity: 0.75 }} />
              </Box>
            }
            onClick={(e) => setTransactionsAnchorEl(e.currentTarget)}
            aria-controls="transactions-menu"
            aria-haspopup="true"
          />
          <Tab
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                Accounting <ArrowDownIcon sx={{ fontSize: 16, opacity: 0.75 }} />
              </Box>
            }
            onClick={(e) => setAccountingAnchorEl(e.currentTarget)}
            aria-controls="accounting-menu"
            aria-haspopup="true"
          />
          <Tab
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                Auction <ArrowDownIcon sx={{ fontSize: 16, opacity: 0.75 }} />
              </Box>
            }
            onClick={(e) => setAuctionAnchorEl(e.currentTarget)}
            aria-controls="auction-menu"
            aria-haspopup="true"
          />
          <Tab
            label="Reports"
            onClick={() => {
              navigate('/reports');
              setValue(6);
            }}
            icon={<ReportsIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
          />
          {isBlackSession ? (
            <Tab
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                  Black <ArrowDownIcon sx={{ fontSize: 16, opacity: 0.75 }} />
                </Box>
              }
              onClick={(e) => setBlackAnchorEl(e.currentTarget)}
              aria-controls="black-menu"
              aria-haspopup="true"
            />
          ) : null}
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: { md: 1 } }}>
          <Button
            color="inherit"
            startIcon={<ProfileIcon />}
            onClick={() => navigate('/profile')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#e2e8f0',
              borderRadius: 2,
              px: 1.25,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            }}
          >
            Profile
          </Button>
          <Button
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#fecaca',
              borderColor: 'rgba(248,113,113,0.45)',
              borderRadius: 2,
              px: 1.25,
              '&:hover': {
                borderColor: '#f87171',
                backgroundColor: 'rgba(248,113,113,0.12)',
                color: '#fee2e2',
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </Toolbar>

      <NavDropdown
        id="master-menu"
        anchorEl={masterAnchorEl}
        onClose={() => setMasterAnchorEl(null)}
        title="Master"
        subtitle="Setup & configuration"
        width={520}
      >
        <Typography sx={sectionLabelSx}>Quick</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35 }}>
          <NavMenuItem icon={<DashboardIcon />} label="Dashboard" hint="Home overview" onClick={() => go(isBlackSession ? '/black' : '/')} />
          <NavMenuItem icon={<ImportStatusIcon />} label="Import Status" hint="Data import jobs" onClick={() => go('/import-status')} />
          <NavMenuItem icon={<SyncIcon />} label="Sync Status" hint="VPS A → VPS B" onClick={() => go('/sync-status')} />
          <NavMenuItem icon={<ReceiptIcon />} label="Billing Sync" hint="Proforma / billing" onClick={() => go('/master/billing-sync')} />
        </Box>

        <Typography sx={sectionLabelSx}>Reference data</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35 }}>
          <NavMenuItem icon={<ArticleIcon />} label="Articles" hint="Metals & %" onClick={() => go('/master/articles')} />
          <NavMenuItem icon={<GroupCodeIcon />} label="Group Codes" hint="Account groups" onClick={() => go('/master/group-codes')} />
          <NavMenuItem icon={<TownVillageIcon />} label="Town / Village" hint="Locations" onClick={() => go('/master/town-village')} />
          <NavMenuItem icon={<CasteIcon />} label="Caste" hint="Party masters" onClick={() => go('/master/caste')} />
          <NavMenuItem icon={<GovInterestIcon />} label="Govt. Interests" hint="Rate & duration" onClick={() => go('/master/government-interests')} />
          <NavMenuItem icon={<LedgerIcon />} label="Ledgers" hint="Chart of accounts" onClick={() => go('/master/ledgers')} />
          <NavMenuItem icon={<FinancialYearIcon />} label="Financial Years" hint="FY periods" onClick={() => go('/master/financial-years')} />
        </Box>

        <Typography sx={sectionLabelSx}>Print & numbering</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35, pb: 0.5 }}>
          <NavMenuItem icon={<TicketIcon />} label="Ticket Settings" hint="Pledge numbers" onClick={() => go('/master/ticket-settings')} />
          <NavMenuItem icon={<ReceiptIcon />} label="Receipt Settings" hint="Receipt numbers" onClick={() => go('/master/receipt-settings')} />
          <NavMenuItem icon={<PrintIcon />} label="Printer Settings" hint="Paper & device" onClick={() => go('/master/printer-settings')} />
          <NavMenuItem icon={<PrintConditionIcon />} label="Print Conditions" hint="Back-page text" onClick={() => go('/master/print-conditions')} />
        </Box>
      </NavDropdown>

      <NavDropdown
        id="pledge-menu"
        anchorEl={pledgeAnchorEl}
        onClose={() => setPledgeAnchorEl(null)}
        title="Pledge"
        subtitle="Loans & releases"
        width={480}
      >
        <Typography sx={sectionLabelSx}>Pledges</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35 }}>
          <NavMenuItem icon={<CreatePledgeIcon />} label="Create Pledge" hint="New loan entry" onClick={() => go('/pledge/create')} />
          <NavMenuItem icon={<CreatePledgeIcon />} label="Create (No Accounting)" hint="Without posting" onClick={() => go('/pledge/create-no-accounting')} />
          <NavMenuItem icon={<ListIcon />} label="All Pledges" hint="Search & manage" onClick={() => go('/pledge/list')} />
          <NavMenuItem icon={<ListIcon />} label="Paid Excess" hint="Excess paid list" onClick={() => go('/pledge/paid-excess')} />
        </Box>
        <Typography sx={sectionLabelSx}>Receipts & release</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35, pb: 0.5 }}>
          <NavMenuItem icon={<ReceiptIcon />} label="Create Receipt" hint="Part payment" onClick={() => go('/pledge/create-receipt')} />
          <NavMenuItem icon={<ReceiptIcon />} label="All Receipts" hint="Receipt history" onClick={() => go('/receipts/all')} />
          <NavMenuItem icon={<ReleaseIcon />} label="Create Release" hint="Close pledge" onClick={() => go('/release')} />
          <NavMenuItem icon={<ReleaseIcon />} label="All Releases" hint="Release history" onClick={() => go('/releases/all')} />
          <NavMenuItem icon={<FingerprintIcon />} label="Find by Fingerprint" hint="Biometric search" onClick={() => go('/pledge/fingerprint-search')} />
        </Box>
      </NavDropdown>

      <NavDropdown
        id="transactions-menu"
        anchorEl={transactionsAnchorEl}
        onClose={() => setTransactionsAnchorEl(null)}
        title="Transactions"
        subtitle="Books & statements"
        width={440}
      >
        <Typography sx={sectionLabelSx}>Registers</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35 }}>
          <NavMenuItem icon={<LedgerIcon />} label="Ledger" hint="Party / account" onClick={() => go('/ledgers')} />
          <NavMenuItem icon={<BookIcon />} label="Journal" hint="Journal register" onClick={() => go('/journal-register')} />
          <NavMenuItem icon={<BookIcon />} label="Daybook" hint="Daily entries" onClick={() => go('/daybook')} />
          <NavMenuItem icon={<BookIcon />} label="Pledge Book" hint="Pledge register" onClick={() => go('/pledge-book')} />
        </Box>
        <Typography sx={sectionLabelSx}>Statements</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35, pb: 0.5 }}>
          <NavMenuItem icon={<ReportsIcon />} label="Profit & Loss" onClick={() => go('/profit-loss')} />
          <NavMenuItem icon={<BalanceIcon />} label="Balance Sheet" onClick={() => go('/balance-sheet')} />
          <NavMenuItem icon={<BugReportIcon />} label="Debug Accounting" hint="Diagnostics" onClick={() => go('/debug-accounting')} />
        </Box>
      </NavDropdown>

      <NavDropdown
        id="accounting-menu"
        anchorEl={accountingAnchorEl}
        onClose={() => setAccountingAnchorEl(null)}
        title="Accounting"
        subtitle="Cash & vouchers"
        width={460}
      >
        <Typography sx={sectionLabelSx}>Receipts & payments</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35 }}>
          <NavMenuItem icon={<ReceiptIcon />} label="Create Receipt" onClick={() => go('/manual-entry?tab=receipt')} />
          <NavMenuItem icon={<ListIcon />} label="All Receipts" onClick={() => go('/receipts/all')} />
          <NavMenuItem icon={<PaymentIcon />} label="Create Payment" onClick={() => go('/manual-entry?tab=payment')} />
          <NavMenuItem icon={<ListIcon />} label="All Payments" onClick={() => go('/payments/all')} />
        </Box>
        <Typography sx={sectionLabelSx}>Tools</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35, pb: 0.5 }}>
          <NavMenuItem icon={<ManualEntryIcon />} label="Journal Voucher" onClick={() => go('/journal-voucher')} />
          <NavMenuItem icon={<CashBalanceIcon />} label="Cash in Hand" onClick={() => go('/cash-balance')} />
          <NavMenuItem icon={<CalculateIcon />} label="Interest Calculation" onClick={() => go('/interest-calculation')} />
          <NavMenuItem icon={<BalanceIcon />} label="Trial Balance" onClick={() => go('/trial-balance')} />
        </Box>
      </NavDropdown>

      <NavDropdown
        id="auction-menu"
        anchorEl={auctionAnchorEl}
        onClose={() => setAuctionAnchorEl(null)}
        title="Auction"
        subtitle="Sale process"
        width={420}
      >
        <Typography sx={sectionLabelSx}>Workflow</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35, pb: 0.5 }}>
          <NavMenuItem icon={<ListIcon />} label="Pre-Auction Catalogue" onClick={() => go('/auction/catalogue')} />
          <NavMenuItem icon={<AuctionIcon />} label="Auction Entry" onClick={() => go('/auction/entry')} />
          <NavMenuItem icon={<ListIcon />} label="Auction List" onClick={() => go('/auction/list')} />
          <NavMenuItem icon={<CashBalanceIcon />} label="Auction Reserve Acc" onClick={() => go('/auction/reserve')} />
          <NavMenuItem icon={<ArticleIcon />} label="Sale Intimation Letter" onClick={() => go('/auction/sale-intimation')} />
          <NavMenuItem icon={<ArticleIcon />} label="Reminder Letter" onClick={() => go('/auction/reminder')} />
        </Box>
      </NavDropdown>

      <NavDropdown
        id="black-menu"
        anchorEl={blackAnchorEl}
        onClose={() => setBlackAnchorEl(null)}
        title="Black"
        subtitle="VPS B extras"
        width={440}
      >
        <Typography sx={sectionLabelSx}>Black book</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.35, px: 0.35, pb: 0.5 }}>
          <NavMenuItem icon={<CreatePledgeIcon />} label="Create Secondary Pledge" hint="Black book entry" onClick={() => go('/pledge/create-secondary')} />
          <NavMenuItem icon={<BlackIcon />} label="Black Daybook" hint="Illegal transactions" onClick={() => go('/black')} />
          <NavMenuItem icon={<LedgerIcon />} label="Vatav Ledger" hint="Vatav accounts" onClick={() => go('/black/vatav-ledger')} />
          <NavMenuItem icon={<ManualEntryIcon />} label="Transfer to Vatav" hint="Move to vatav" onClick={() => go('/black/vatav-transfer')} />
          <NavMenuItem icon={<CashBalanceIcon />} label="Cash in Hand (Illegal)" hint="Black cash" onClick={() => go('/illegal-cash-balance')} />
        </Box>
      </NavDropdown>
    </AppBar>
  );
};

export default Navbar;
